import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, LineChart, BarList } from '@tremor/react';
import { KPICard } from '@/components/KPICard';
import { getQualidadeVA, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /qualidade-va?dias=N) ─────────────────────────────

interface TimelinePonto {
  dia: string;
  n_validos: number;
  n_limpos: number;
  pct_limpo: number | null;
  def_por_caso: number | null;
}

interface Timeline {
  serie: TimelinePonto[];
  n_validos_total: number;
  n_limpos_total: number;
  pct_limpo_total: number | null;
}

interface ClusterDefeito {
  verba: string;
  tipo: string;
  n: number;
}

interface QualidadeVAData {
  disponivel: boolean;
  erro?: string;
  dias: number;
  hoje: string;
  timeline: Timeline;
  top_clusters_defeito: ClusterDefeito[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const PERIODOS = [14, 21, 30];

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined, decimals = 0): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtDia(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function QualidadeVAPage() {
  const [dias, setDias] = useState(21);

  const { data, isLoading, error } = useQuery<QualidadeVAData>({
    queryKey: ['qualidade-va', dias],
    queryFn: async () => (await getQualidadeVA(dias)).data,
    refetchInterval: 5 * 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar qualidade')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const tl = data?.timeline;

  const pctChart = useMemo(
    () =>
      (tl?.serie ?? []).map((p) => ({
        dia: fmtDia(p.dia),
        '% limpo': p.pct_limpo !== null ? +(p.pct_limpo * 100).toFixed(1) : null,
      })),
    [tl],
  );

  const defChart = useMemo(
    () =>
      (tl?.serie ?? []).map((p) => ({
        dia: fmtDia(p.dia),
        'Defeitos/caso': p.def_por_caso ?? 0,
      })),
    [tl],
  );

  const clusterItems = (data?.top_clusters_defeito ?? []).map((c) => ({
    name: `${c.verba} · ${c.tipo}`,
    value: c.n,
  }));

  // Tendência: primeiro vs último ponto da série (sinal simples de subindo/descendo)
  const serie = tl?.serie ?? [];
  const primeiro = serie.find((p) => p.pct_limpo !== null);
  const ultimo = [...serie].reverse().find((p) => p.pct_limpo !== null);
  const deltaTendencia =
    primeiro && ultimo && primeiro !== ultimo && primeiro.pct_limpo !== null && ultimo.pct_limpo !== null
      ? ultimo.pct_limpo - primeiro.pct_limpo
      : null;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>Qualidade (VA vs perito)</Title>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDias(p)}
                className={`rounded px-2.5 py-1 text-xs transition ${
                  dias === p
                    ? 'bg-blue-600/30 text-blue-200'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
        <Text>
          Tendência da qualidade ENTREGUE medida vs o dispositivo real pelo juiz-régua
          (cron diário) — NÃO é estorno. % limpo = casos sem defeito confirmado pelo
          2º juiz adversarial / total de casos trabalhistas válidos com cálculo.
        </Text>
      </header>

      {!isLoading && errMsg && (
        <Card className="border border-red-800/40 bg-red-900/10">
          <Text className="text-red-300">{errMsg}</Text>
        </Card>
      )}

      <Grid numItems={1} numItemsSm={3} className="gap-4">
        <KPICard
          label={`% limpo (${dias}d, agregado)`}
          value={isLoading ? null : fmtPct(tl?.pct_limpo_total)}
          delta={isLoading ? null : deltaTendencia}
          loading={isLoading}
          error={errMsg}
          hint={`${fmtInt(tl?.n_limpos_total)} de ${fmtInt(tl?.n_validos_total)} casos válidos sem defeito confirmado`}
        />
        <KPICard
          label="Casos válidos avaliados"
          value={isLoading ? null : fmtInt(tl?.n_validos_total)}
          loading={isLoading}
          error={errMsg}
          hint="trabalhista + com cálculo (doc_valido AND escopo AND NOT sem_calculo)"
        />
        <KPICard
          label="Tendência (1º → último dia c/ dado)"
          value={isLoading ? null : deltaTendencia === null ? '—' : `${deltaTendencia >= 0 ? '+' : ''}${(deltaTendencia * 100).toFixed(0)}pp`}
          loading={isLoading}
          error={errMsg}
          hint="pontos percentuais de % limpo no período selecionado"
        />
      </Grid>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title>% limpo por dia</Title>
          <Text className="text-xs text-slate-400">
            Sobe = entrega mais perto do perito.
          </Text>
          {isLoading && <Text className="mt-3">carregando…</Text>}
          {!isLoading && !errMsg && (
            <LineChart
              className="mt-3 h-64"
              data={pctChart}
              index="dia"
              categories={['% limpo']}
              colors={['emerald']}
              valueFormatter={(v) => `${v}%`}
              showAnimation
              connectNulls
            />
          )}
        </Card>

        <Card>
          <Title>Defeitos confirmados / caso</Title>
          <Text className="text-xs text-slate-400">
            Média de defeitos confirmados pelo 2º juiz por caso válido, por dia.
          </Text>
          {isLoading && <Text className="mt-3">carregando…</Text>}
          {!isLoading && !errMsg && (
            <LineChart
              className="mt-3 h-64"
              data={defChart}
              index="dia"
              categories={['Defeitos/caso']}
              colors={['amber']}
              showAnimation
            />
          )}
        </Card>
      </Grid>

      <Card>
        <Title>Top clusters de defeito confirmado</Title>
        <Text className="text-xs text-slate-400">
          Por verba × tipo (veredito_2='real' do 2º juiz) — onde a qualidade dói.
        </Text>
        {isLoading && <Text className="mt-3">carregando…</Text>}
        {!isLoading && !errMsg && (
          <>
            {clusterItems.length === 0 ? (
              <Text className="mt-3 text-sm text-slate-400">
                sem defeitos confirmados no momento
              </Text>
            ) : (
              <BarList data={clusterItems} className="mt-3" valueFormatter={fmtInt} color="rose" />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
